import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { filterReleaseNotes, releaseNotes, type ReleaseNote } from '@trackly/shared';
import { useGetFeatureFlagsQuery } from '../services/FeatureFlagService';

const SEEN_KEY_PREFIX = 'trackly.whatsNew.seen.';

// null means storage is unreadable. The caller then treats every note as seen, so a user with
// blocked storage never gets a badge that cannot clear.
function readSeen(key: string): Set<string> | null {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(key) ?? '[]');
    return new Set(Array.isArray(stored) ? stored.filter((id): id is string => typeof id === 'string') : []);
  } catch {
    return null;
  }
}

function writeSeen(key: string, ids: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // Storage is blocked or full. The notes show as seen again on the next load.
  }
}

function unseenIdsOf(notes: ReleaseNote[], seen: Set<string> | null): string[] {
  return seen ? notes.filter((n) => !seen.has(n.id)).map((n) => n.id) : [];
}

function mergeSeen(stored: Set<string> | null, inMemory: string[] | undefined): Set<string> | null {
  return stored && new Set([...stored, ...(inMemory ?? [])]);
}

/**
 * The release notes for the web, behind the `WhatsNew` flag (ADR 0010). Fails closed like
 * `useFeatureFlag`. "Seen" is stored per signed-in account in the browser. It is also kept in
 * memory, so opening the dialog clears the badge even when the browser cannot save it.
 */
export function useWhatsNew() {
  const { data, isError } = useGetFeatureFlagsQuery();
  const { instance, accounts } = useMsal();
  const [sessionSeen, setSessionSeen] = useState<Record<string, string[]>>({});

  const enabled = !isError && data?.flags?.WhatsNew === true;
  const notes: ReleaseNote[] = enabled ? filterReleaseNotes(releaseNotes, 'web', data?.flags) : [];

  // The active account is the one API requests use (createAuthenticatedBaseQuery).
  const account = instance.getActiveAccount() ?? accounts[0];
  const key = SEEN_KEY_PREFIX + (account?.homeAccountId ?? '');
  const seen = mergeSeen(readSeen(key), sessionSeen[key]);
  const unseenIds = unseenIdsOf(notes, seen);

  const markSeen = () => {
    if (unseenIds.length === 0) return;
    const all = [...(seen ?? []), ...unseenIds];
    writeSeen(key, all);
    setSessionSeen((previous) => ({ ...previous, [key]: all }));
  };

  return { enabled, notes, unseenIds, markSeen };
}
